/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import RuntimeConfigStore from '@app/stores/RuntimeConfigStore';

export const MAX_SOUNDBOARD_DURATION_SECONDS = 10;

// Keep the re-encoded clip small (mono, modest sample rate) so the base64 upload stays well under the
// server's soundboard size limit. The backend stores the raw bytes and serves them back unchanged.
const TARGET_SAMPLE_RATE = 32000;

export function getSoundboardSoundURL(id: string): string {
	const base = RuntimeConfigStore.mediaEndpoint;
	if (!base) {
		return '';
	}
	return `${base.replace(/\/$/, '')}/soundboard-sounds/${id}`;
}

export async function decodeAudioFile(file: File): Promise<AudioBuffer> {
	const arrayBuffer = await file.arrayBuffer();
	const ctx = new OfflineAudioContext(1, 1, TARGET_SAMPLE_RATE);
	const decoded = await ctx.decodeAudioData(arrayBuffer);
	return decoded;
}

export function computeWaveformPeaks(buffer: AudioBuffer, numBars: number): Array<number> {
	const data = buffer.getChannelData(0);
	const blockSize = Math.max(1, Math.floor(data.length / numBars));
	const peaks: Array<number> = [];
	for (let i = 0; i < numBars; i++) {
		let max = 0;
		const start = i * blockSize;
		const end = Math.min(start + blockSize, data.length);
		for (let j = start; j < end; j++) {
			const v = Math.abs(data[j] ?? 0);
			if (v > max) {
				max = v;
			}
		}
		peaks.push(max);
	}
	return peaks;
}

async function renderTrimmedMono(buffer: AudioBuffer, startSec: number, endSec: number): Promise<AudioBuffer> {
	const sampleRate = Math.min(buffer.sampleRate, TARGET_SAMPLE_RATE);
	const clampedStart = Math.max(0, Math.min(startSec, buffer.duration));
	const rawDuration = Math.max(0, Math.min(endSec, buffer.duration) - clampedStart);
	const duration = Math.min(rawDuration, MAX_SOUNDBOARD_DURATION_SECONDS);
	const frameCount = Math.max(1, Math.floor(duration * sampleRate));

	const offline = new OfflineAudioContext(1, frameCount, sampleRate);
	const source = offline.createBufferSource();
	source.buffer = buffer;
	source.connect(offline.destination);
	source.start(0, clampedStart, duration);
	return await offline.startRendering();
}

function encodeWav(buffer: AudioBuffer): ArrayBuffer {
	const numChannels = 1;
	const sampleRate = buffer.sampleRate;
	const samples = buffer.getChannelData(0);
	const bytesPerSample = 2;
	const blockAlign = numChannels * bytesPerSample;
	const dataSize = samples.length * bytesPerSample;
	const out = new ArrayBuffer(44 + dataSize);
	const view = new DataView(out);

	const writeString = (offset: number, str: string) => {
		for (let i = 0; i < str.length; i++) {
			view.setUint8(offset + i, str.charCodeAt(i));
		}
	};

	writeString(0, 'RIFF');
	view.setUint32(4, 36 + dataSize, true);
	writeString(8, 'WAVE');
	writeString(12, 'fmt ');
	view.setUint32(16, 16, true);
	view.setUint16(20, 1, true);
	view.setUint16(22, numChannels, true);
	view.setUint32(24, sampleRate, true);
	view.setUint32(28, sampleRate * blockAlign, true);
	view.setUint16(32, blockAlign, true);
	view.setUint16(34, 16, true);
	writeString(36, 'data');
	view.setUint32(40, dataSize, true);

	let offset = 44;
	for (let i = 0; i < samples.length; i++) {
		let sample = Math.max(-1, Math.min(1, samples[i]));
		sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
		view.setInt16(offset, sample, true);
		offset += 2;
	}

	return out;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
	const bytes = new Uint8Array(buffer);
	let binary = '';
	const chunkSize = 0x8000;
	for (let i = 0; i < bytes.length; i += chunkSize) {
		const chunk = bytes.subarray(i, i + chunkSize);
		binary += String.fromCharCode.apply(null, chunk as unknown as Array<number>);
	}
	return btoa(binary);
}

export async function trimAudioToBase64Wav(
	buffer: AudioBuffer,
	startSec: number,
	endSec: number,
): Promise<{base64: string; byteLength: number}> {
	const rendered = await renderTrimmedMono(buffer, startSec, endSec);
	const wav = encodeWav(rendered);
	return {base64: arrayBufferToBase64(wav), byteLength: wav.byteLength};
}
